import { useCallback } from 'react';

import type { EditorActions } from '@atlaskit/editor-core';

import { fg } from '@confluence/feature-gating';
import type { FlagsStateContainer } from '@confluence/flags/entry-points/FlagsStateContainer.ts';

import { filterToMarkdownSafeAdf } from './filterToMarkdownSafeAdf.ts';
import { isMarkdownConversionLossy } from './isMarkdownConversionLossy.ts';
import {
	useMarkdownLossyDialogActions,
	useMarkdownLossyDialogState,
} from './MarkdownLossyDialogStore.ts';
import { useIsMarkdownMode } from './useIsMarkdownMode.ts';
import { useMarkdownConvertFlags } from './useMarkdownConvertFlags.tsx';
import { useUpdateMarkdownMode } from './useUpdateMarkdownMode.ts';

/**
 * Mirrors `EditorObjectHeaderContext.editorActions`: either the real
 * `EditorActions` class (when the editor is mounted) or a minimal
 * placeholder with just `isDocumentEmpty` (when it isn't).
 */
type DefaultNoop = () => void;
type EmptyEditorActions = {
	isDocumentEmpty: DefaultNoop;
};
export type EditorActionsOrEmpty = EditorActions | EmptyEditorActions;

/** Optional subset of `FlagsStateContainer` we call. `unknown` returns let void-returning stubs satisfy the type. */
export type FlagsLike = {
	[K in 'showErrorFlag' | 'showFlag' | 'hideFlag']?: (
		...args: Parameters<FlagsStateContainer[K]>
	) => unknown;
};

type UseMarkdownModeActionsArgs = {
	contentId: string;
	isLivePage: boolean;
	editorActions: EditorActionsOrEmpty;
	flags: FlagsLike;
};

export type UseMarkdownModeActionsResult = {
	isMarkdownMode: boolean;
	isUpdating: boolean;
	isLossyDialogOpen: boolean;
	closeLossyDialog: () => void;
	convertToMarkdown: () => Promise<void>;
	convertToRichText: () => Promise<void>;
	confirmLossyConvertToMarkdown: () => Promise<{ success: boolean }>;
};

/**
 * Encapsulates the markdown-mode toggle flow for a live page:
 *  - reads current mode + mutation loading state,
 *  - drains in-flight collab steps before flipping the BE flag (avoids
 *    NCS-restore-replay duplication, see `drainUnconfirmedSteps`),
 *  - opens a confirmation dialog when conversion is lossy,
 *  - on dialog-confirm, rewrites the doc with the markdown-safe filter so
 *    the user immediately sees what will be persisted.
 */
export const useMarkdownModeActions = ({
	contentId,
	isLivePage,
	editorActions,
	flags,
}: UseMarkdownModeActionsArgs): UseMarkdownModeActionsResult => {
	const { updateMarkdownMode, loading: isUpdating } = useUpdateMarkdownMode();
	const isMarkdownMode = useIsMarkdownMode(isLivePage ? contentId : undefined);
	// Dialog open/close lives in a sweet-state store so multiple call sites
	// (header overflow menu, title `.md`-suffix gesture) all drive the same
	// dialog instance rendered by `MoreActions`.
	const [{ isLossyDialogOpen }] = useMarkdownLossyDialogState();
	const { openLossyDialog, closeLossyDialog } = useMarkdownLossyDialogActions();
	const convertFlags = useMarkdownConvertFlags(flags);

	// Wait for any unconfirmed collab steps to be acked before flipping the
	// markdown-mode flag. The convert mutation invalidates the live-doc
	// session on the BE, which triggers an NCS restore that would otherwise
	// re-apply our unconfirmed steps on top of the server doc that already
	// has them — duplicating anything typed just before the click.
	const drainUnconfirmedSteps = useCallback(async () => {
		if (!('getResolvedEditorState' in editorActions)) {
			return;
		}
		try {
			await editorActions.getResolvedEditorState('publish');
		} catch {
			// Swallow — don't block convert if the drain fails.
		}
	}, [editorActions]);

	// Flip the BE markdown-mode flag. Does NOT touch the editor doc — for
	// non-lossy ADF the existing content round-trips cleanly, and calling
	// `replaceDocument` here would re-init editor state and leave the
	// rich→md transition stuck in a loading state. The dialog-confirm path
	// (`confirmLossyConvertToMarkdown`) handles lossy rewrites separately.
	const flipFlagToMarkdown = useCallback(async () => {
		await drainUnconfirmedSteps();
		const result = await updateMarkdownMode(contentId, true);
		if (!result.success) {
			convertFlags.showError('toMarkdown');
			return { success: false };
		}
		convertFlags.showSuccess('toMarkdown');
		return { success: true };
	}, [contentId, convertFlags, drainUnconfirmedSteps, updateMarkdownMode]);

	// Lossy path: after the BE flag flip, strip nodes/marks that won't
	// survive markdown round-trip and push the filtered ADF back into the
	// editor so the user sees the final state immediately.
	//
	// Gated behind `cc-disable-be-md-conversion` — the BE now owns the
	// rewrite, and this FE filter only runs when the BE conversion is
	// killed via that gate.
	const confirmLossyConvertToMarkdown = useCallback(async () => {
		const result = await flipFlagToMarkdown();
		if (!result.success) {
			return result;
		}

		if (
			'replaceDocument' in editorActions &&
			'getValue' in editorActions &&
			fg('cc-disable-be-md-conversion')
		) {
			try {
				const currentAdf = await editorActions.getValue();
				const filteredAdf = filterToMarkdownSafeAdf(currentAdf);
				if (filteredAdf) {
					editorActions.replaceDocument(filteredAdf);
				}
			} catch {
				// Editor may already be unmounted; the flag flip succeeded so
				// the next mount will load the rewritten doc from the BE.
			}
		}

		return { success: true };
	}, [editorActions, flipFlagToMarkdown]);

	// Entry point for the "Convert to markdown" menu item. Inspect the doc:
	// if it has rich content that won't round-trip, open the dialog;
	// otherwise just flip the flag.
	const convertToMarkdown = useCallback(async () => {
		let adf: unknown;
		if ('getValue' in editorActions) {
			try {
				adf = await editorActions.getValue();
			} catch {
				adf = undefined;
			}
		}

		if (isMarkdownConversionLossy(adf)) {
			openLossyDialog();
			return;
		}

		await flipFlagToMarkdown();
	}, [editorActions, flipFlagToMarkdown, openLossyDialog]);

	// md → rich uses the same drain path; the BE-driven session restart
	// produces the same potential duplication.
	const convertToRichText = useCallback(async () => {
		await drainUnconfirmedSteps();
		const result = await updateMarkdownMode(contentId, false);
		if (!result.success) {
			convertFlags.showError('toRichText');
			return;
		}
		convertFlags.showSuccess('toRichText');
	}, [contentId, convertFlags, drainUnconfirmedSteps, updateMarkdownMode]);

	return {
		isMarkdownMode,
		isUpdating,
		isLossyDialogOpen,
		closeLossyDialog,
		convertToMarkdown,
		convertToRichText,
		confirmLossyConvertToMarkdown,
	};
};
